import { Box, Button, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { AzureOnboardAgentSection } from './AzureOnboardAgentSection';
import type { ParsedOutput } from './parseTenantOutput';
import { PasteTenantOutputDialog } from './pasteTenantOutputDialog';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { extractErrorMessage } from '../errorHandling';
import { NumericBulletSection } from '../numericBulletSection';

export interface AzureTenantState {
  backup_app_service_principal_id: string;
  restore_app_name: string;
  tenantId: string;
  tenantConnectMethod: 'terraform' | 'bash';
  onboardingFlow: boolean;
  subscriptionId: string;
  restore_app_application_id: string;
  restore_app_service_principal_id: string;
  managed_identity_resource_id: string;

  // Agent app fields are optional (only present when create_agent_resources=true in onboarding)
  agent_app_application_id?: string;
  agent_app_service_principal_id?: string;
  agent_managed_identity_resource_id?: string;
}

export const ConnectAzureTenant = <T extends AzureTenantState>(
  props: StepProps<T>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isPasteDialogOpen, setIsPasteDialogOpen] = useState(false);
  const dal = useDAL();
  const { data: backupApps, isLoading } =
    dal.cloudAccounts.azureApps.useApps('Scanning');
  const { isDev, isDemo } = useEnvironment();
  const { SapHanaPitrSupport } = useFeatureFlags();
  const [onboardAgentApp, setOnboardAgentApp] = useState(!!SapHanaPitrSupport);
  const tenantIdConfig = getCloudFieldConfig(Provider.Azure, 'tenantId');
  const subscriptionIdConfig = getCloudFieldConfig(
    Provider.Azure,
    'subscriptionId'
  );
  const appIdConfig = getCloudFieldConfig(Provider.Azure, 'applicationId');
  const servicePrincipalIdConfig = getCloudFieldConfig(
    Provider.Azure,
    'servicePrincipalId'
  );
  const managedIdentityResourceIdConfig = getCloudFieldConfig(
    Provider.Azure,
    'managedIdentityResourceId'
  );

  const terraformCode = `\
module "azure_tenant_onboarding" {
  source = "https://eon.blob.core.windows.net/public/onboarding/latest/tenant/terraform/tenant.zip"

  eon_backup_app_id = "${backupApps?.backupAppId ?? ''}"
}

output "tenant_onboarding" {
  value = module.azure_tenant_onboarding
}`;

  const bashCode = `\
# Log in to the tenant
az login --tenant "${props.wizardState.tenantId}"

# Set the management subscription ID
az account set --subscription "${props.wizardState.subscriptionId}"

# Get access tokens from Azure
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Download and run tenant onboarding script
curl -fsSL https://eon.blob.core.windows.net/public/onboarding/latest/tenant/bash/tenant.sh -o tenant.sh && chmod +x tenant.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./tenant.sh \\
  --tenant-id "${props.wizardState.tenantId}" \\
  --subscription-id "${props.wizardState.subscriptionId}" \\
  --eon-backup-app-id "${backupApps?.backupAppId ?? ''}"
`;

  const code =
    props.wizardState.tenantConnectMethod === 'terraform'
      ? terraformCode
      : bashCode;

  const missingFirstStepFields =
    !props.wizardState.tenantId || !props.wizardState.subscriptionId;
  const agentFieldsIncomplete =
    onboardAgentApp &&
    (!props.wizardState.agent_app_application_id ||
      !props.wizardState.agent_app_service_principal_id ||
      !props.wizardState.agent_managed_identity_resource_id);
  const missingSecondStepFields =
    !props.wizardState.backup_app_service_principal_id ||
    !props.wizardState.restore_app_application_id ||
    !props.wizardState.restore_app_service_principal_id ||
    !props.wizardState.managed_identity_resource_id ||
    agentFieldsIncomplete;
  const isMissingFields = missingFirstStepFields || missingSecondStepFields;

  const validateForm = () => {
    if (isDev || isDemo) {
      return '';
    }

    const validationErrors =
      !tenantIdConfig.validation.validateInput(props.wizardState.tenantId) ||
      !subscriptionIdConfig.validation.validateInput(
        props.wizardState.subscriptionId
      ) ||
      !servicePrincipalIdConfig.validation.validateInput(
        props.wizardState.backup_app_service_principal_id
      ) ||
      !appIdConfig.validation.validateInput(
        props.wizardState.restore_app_application_id
      ) ||
      !servicePrincipalIdConfig.validation.validateInput(
        props.wizardState.restore_app_service_principal_id
      ) ||
      !managedIdentityResourceIdConfig.validation.validateInput(
        props.wizardState.managed_identity_resource_id
      ) ||
      (onboardAgentApp &&
        (!props.wizardState.agent_app_application_id ||
          !props.wizardState.agent_app_service_principal_id ||
          !props.wizardState.agent_managed_identity_resource_id ||
          !appIdConfig.validation.validateInput(
            props.wizardState.agent_app_application_id
          ) ||
          !servicePrincipalIdConfig.validation.validateInput(
            props.wizardState.agent_app_service_principal_id
          ) ||
          !managedIdentityResourceIdConfig.validation.validateInput(
            props.wizardState.agent_managed_identity_resource_id
          )));

    if (validationErrors && isMissingFields) {
      return 'All fields are required, and some are in the wrong format. Fix the issues so you can proceed.';
    }

    if (validationErrors) {
      return 'Some fields are in the wrong format. Fix the issues so you can proceed.';
    }

    if (isMissingFields) {
      return 'All fields are required, but some are missing values. Fill in all fields so you can proceed.';
    }

    return '';
  };

  const onNextClick = async () => {
    try {
      setError(null);
      setIsDeploying(true);
      await dal.cloudAccounts.azureApps.connect({
        eonBackupServicePrincipalId:
          props.wizardState.backup_app_service_principal_id,
        customerBackupApplicationId:
          props.wizardState.restore_app_application_id,
        customerBackupServicePrincipalId:
          props.wizardState.restore_app_service_principal_id,
        customerTenantId: props.wizardState.tenantId,
        federatedManagedIdentityResourceId:
          props.wizardState.managed_identity_resource_id,
        agentApplicationId: onboardAgentApp
          ? props.wizardState.agent_app_application_id
          : undefined,
        agentServicePrincipalId: onboardAgentApp
          ? props.wizardState.agent_app_service_principal_id
          : undefined,
        agentFederatedManagedIdentityResourceId: onboardAgentApp
          ? props.wizardState.agent_managed_identity_resource_id
          : undefined,
      });

      if (props.wizardState.onboardingFlow) {
        props.setNextStep(props.currentStep.next?.[0]);
      } else {
        await props.finish();
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const nextButtonTooltip = validateForm();

  const handleParseOutput = (parsed: ParsedOutput) => {
    props.setWizardState((prevState) => ({
      ...prevState,
      ...parsed,
    }));
  };

  return (
    <>
      <StepContainer
        hideStepper={!props.wizardState.onboardingFlow}
        stepperIndex={0}
        stepperLabels={props.stepperLabels}
        onBackClick={props.back}
        canGoNext={() => !isDeploying && !nextButtonTooltip}
        nextButtonDisabledTooltip={nextButtonTooltip}
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text={`To get started, we need a subscription to install Eon Azure apps, service principals, and other resources that manage Eon's access.
              Make sure the Azure tenant also contains the resources you want to back up later.`}
            connectMethod={props.wizardState.tenantConnectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({
                ...props.wizardState,
                tenantConnectMethod: connectMethod,
              });
            }}
          />
          <Box
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack
                gap='40px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                <NumericBulletSection
                  bulletNumber={1}
                  title="Deploy Eon's management resources"
                >
                  <BulletItem
                    title={`Tell us where you want Eon's management resources installed, and then ${props.wizardState.tenantConnectMethod === 'terraform' ? 'apply the generated Terraform module' : 'run the generated Bash command'} in your environment.`}
                    leftContent={
                      <Stack gap='24px'>
                        <FormTextField
                          title='Azure tenant ID'
                          validation={tenantIdConfig.validation}
                          placeholder={tenantIdConfig.placeholder}
                          value={props.wizardState.tenantId || ''}
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              tenantId: event.target.value,
                            })
                          }
                        />
                        <FormTextField
                          title='Management subscription ID'
                          validation={subscriptionIdConfig.validation}
                          placeholder={subscriptionIdConfig.placeholder}
                          value={props.wizardState.subscriptionId || ''}
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              subscriptionId: event.target.value,
                            })
                          }
                        />
                      </Stack>
                    }
                    rightContent={
                      <CopyableCode
                        title={
                          props.wizardState.tenantConnectMethod === 'terraform'
                            ? 'main.tf'
                            : 'tenant.sh'
                        }
                        code={code}
                        blur={missingFirstStepFields}
                      />
                    }
                  />
                </NumericBulletSection>
                <NumericBulletSection
                  bulletNumber={2}
                  title='Complete configuration'
                >
                  <BulletItem
                    title={`After deployment, values for the below fields are returned in the command line. Paste the values, or use the 'Paste Output' button to automatically populate them.`}
                    leftContent={
                      <Stack gap='24px' sx={{ mb: '24px' }}>
                        <Button
                          variant='text'
                          startIcon={
                            <i className='material-symbols-content-paste' />
                          }
                          onClick={() => setIsPasteDialogOpen(true)}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Paste Output
                        </Button>
                        <FormTextField
                          title='backup_app_service_principal_id'
                          validation={servicePrincipalIdConfig.validation}
                          placeholder={servicePrincipalIdConfig.placeholder}
                          value={
                            props.wizardState.backup_app_service_principal_id ||
                            ''
                          }
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              backup_app_service_principal_id:
                                event.target.value,
                            })
                          }
                        />
                        <FormTextField
                          title='restore_app_application_id'
                          validation={appIdConfig.validation}
                          placeholder={appIdConfig.placeholder}
                          value={
                            props.wizardState.restore_app_application_id || ''
                          }
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              restore_app_application_id: event.target.value,
                            })
                          }
                        />
                        <FormTextField
                          title='restore_app_service_principal_id'
                          validation={servicePrincipalIdConfig.validation}
                          placeholder={servicePrincipalIdConfig.placeholder}
                          value={
                            props.wizardState
                              .restore_app_service_principal_id || ''
                          }
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              restore_app_service_principal_id:
                                event.target.value,
                            })
                          }
                        />
                        <FormTextField
                          title='managed_identity_resource_id'
                          validation={
                            managedIdentityResourceIdConfig.validation
                          }
                          placeholder={
                            managedIdentityResourceIdConfig.placeholder
                          }
                          value={
                            props.wizardState.managed_identity_resource_id || ''
                          }
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              managed_identity_resource_id: event.target.value,
                            })
                          }
                        />
                        <AzureOnboardAgentSection
                          visible={!!SapHanaPitrSupport}
                          onboardAgentApp={onboardAgentApp}
                          setOnboardAgentApp={setOnboardAgentApp}
                          agentAppApplicationId={
                            props.wizardState.agent_app_application_id || ''
                          }
                          agentAppServicePrincipalId={
                            props.wizardState.agent_app_service_principal_id ||
                            ''
                          }
                          agentManagedIdentityResourceId={
                            props.wizardState
                              .agent_managed_identity_resource_id || ''
                          }
                          onAgentStateChange={(update) =>
                            props.setWizardState({
                              ...props.wizardState,
                              ...update,
                            })
                          }
                          appIdConfig={appIdConfig}
                          servicePrincipalIdConfig={servicePrincipalIdConfig}
                          managedIdentityResourceIdConfig={
                            managedIdentityResourceIdConfig
                          }
                        />
                      </Stack>
                    }
                  />
                </NumericBulletSection>
              </Stack>
            )}
          </Box>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => setError(null)}
        error={error}
      />
      <PasteTenantOutputDialog
        isOpen={isPasteDialogOpen}
        onClose={() => setIsPasteDialogOpen(false)}
        onParse={handleParseOutput}
        connectMethod={props.wizardState.tenantConnectMethod}
      />
    </>
  );
};
